// ============================================================================
// DINEOPS HOOKS
// ============================================================================
// React hooks over dineOpsService.

import { useState, useEffect, useCallback } from 'react';
import { dineOpsService } from 'api/services/dineOpsService';
import type {
  ID,
  DineOutlet,
  DineCourse,
  DineMenuGroup,
  DineItem,
  DineItemSearchRequest,
  DineQueryRequest,
  DineTagGroup,
  DineAddonGroup,
  DineKitchenStation,
  DineRequisitionPurpose,
} from 'api/types';

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const emptyPagination: Pagination = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Shared paged-fetch hook.
 *
 * `paramsKey` is the serialised params, and it is what the effect depends on —
 * not the params object. A caller passing an object literal creates a new
 * reference every render, and depending on the object itself is an infinite
 * fetch loop that looks like a slow screen rather than a bug.
 */
function usePaged<T, P>(
  fetcher: (params?: P) => Promise<{ data: T[]; pagination: Pagination }>,
  params?: P,
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>(emptyPagination);

  const paramsKey = JSON.stringify(params ?? {});

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher(params);
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, pagination, refetch };
}

export const useDineItems = (params?: DineItemSearchRequest) =>
  usePaged<DineItem, DineItemSearchRequest>((p) => dineOpsService.getItems(p), params);

export const useDineCourses = (params?: DineQueryRequest) =>
  usePaged<DineCourse, DineQueryRequest>((p) => dineOpsService.getCourses(p), params);

export const useDineMenuGroups = (params?: DineQueryRequest) =>
  usePaged<DineMenuGroup, DineQueryRequest>((p) => dineOpsService.getGroups(p), params);

export const useDineTagGroups = (params?: DineQueryRequest) =>
  usePaged<DineTagGroup, DineQueryRequest>((p) => dineOpsService.getTagGroups(p), params);

export const useDineAddonGroups = (params?: DineQueryRequest) =>
  usePaged<DineAddonGroup, DineQueryRequest>((p) => dineOpsService.getAddonGroups(p), params);

export const useDineStations = (params?: DineQueryRequest) =>
  usePaged<DineKitchenStation, DineQueryRequest>((p) => dineOpsService.getStations(p), params);

/**
 * The outlet the user is working in. Fetched once; everything else hangs off it.
 *
 * A 403 here is the module switched off, not a fault — the screen should say so
 * rather than show an empty grid and let someone conclude the card is empty.
 */
export const useDineOutlet = () => {
  const [outlet, setOutlet] = useState<DineOutlet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOutlet(await dineOpsService.getOutlet());
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { outlet, loading, error, refetch };
};

/**
 * The tenant's requisition purposes (2026-10-26), by display order. Raising a requisition offers the
 * active ones; filters and the Purposes window ask for inactive ones too. Re-fetched on refetch(), e.g.
 * after the Purposes window saves, so a new purpose is offered straight away.
 */
export const useRequisitionPurposes = (includeInactive = false) => {
  const [purposes, setPurposes] = useState<DineRequisitionPurpose[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setPurposes(await dineOpsService.getRequisitionPurposes(includeInactive));
    } catch {
      setPurposes([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { purposes, loading, refetch };
};

/** Deletes a dish. rowVersion is required: the API refuses a stale one with 409. */
export const useDeleteDineItem = () => {
  const [loading, setLoading] = useState(false);

  const remove = useCallback(async (id: ID, rowVersion: number) => {
    setLoading(true);
    try {
      await dineOpsService.deleteItem(id, rowVersion);
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading };
};
